import type { Rating, CardStatus, Sm2Config } from '../lib/spacedRepetition/sm2';

const SG_TZ = 'Asia/Singapore';

function sgParts(date: Date) {
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone: SG_TZ,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const parts = f.formatToParts(date);
  const m: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') m[p.type] = p.value;
  }
  const ap = (m.dayPeriod ?? '').replace(/\s/g, '').toUpperCase();
  const time = `${m.hour}:${m.minute}${ap}`;
  const dayKey = `${m.year}-${m.month}-${m.day}`;
  return { time, dayKey, day: m.day, month: m.month };
}

/** e.g. "yesterday 10:08PM", "today 3:15PM", "10 May 08:21PM" */
export function formatFlashcardLastSeen(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const d = sgParts(date);
  const n = sgParts(now);

  let dayLabel: string;
  if (d.dayKey === n.dayKey) {
    dayLabel = 'today';
  } else {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const y = sgParts(yesterday);
    if (d.dayKey === y.dayKey) {
      dayLabel = 'yesterday';
    } else {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIdx = parseInt(d.month, 10) - 1;
      dayLabel = `${parseInt(d.day, 10)} ${monthNames[monthIdx] ?? d.month}`;
    }
  }

  return `${dayLabel} ${d.time}`;
}

/** Singapore calendar day key for grouping logs. */
export function flashcardSgDayKey(iso: string): string {
  return sgParts(new Date(iso)).dayKey;
}

/** Time only, e.g. "10:25AM" (Singapore). */
export function formatFlashcardLogTime(iso: string): string {
  return sgParts(new Date(iso)).time;
}

/** Section heading for a study-history day group. */
export function formatFlashcardDayHeader(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const d = sgParts(date);
  const n = sgParts(now);

  if (d.dayKey === n.dayKey) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.dayKey === sgParts(yesterday).dayKey) return 'Yesterday';

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIdx = parseInt(d.month, 10) - 1;
  return `${parseInt(d.day, 10)} ${monthNames[monthIdx] ?? d.month}`;
}

export function formatAgainIntervalHint(
  progress: { status: CardStatus } | null,
  config: Sm2Config
): string {
  const minutes = config.learningStepsMinutes[0] ?? 1;
  if (minutes < 60) {
    return minutes === 1 ? '~1 min' : `~${minutes} min`;
  }
  if (minutes < 1440) {
    const h = Math.round(minutes / 60);
    return h === 1 ? '~1 hr' : `~${h} hrs`;
  }
  if (minutes === 1440) return '~1 day';
  const days = Math.round(minutes / 1440);
  return `~${days} days`;
}

export function ratingButtonHints(
  progress: { status: CardStatus; intervalDays?: number; easeFactor?: number | null } | null,
  config: Sm2Config
): Record<Rating, string | undefined> {
  const again = formatAgainIntervalHint(progress, config);
  return {
    again,
    hard: progress?.status === 'review' ? 'shorter gap' : undefined,
    good: undefined,
    easy: 'longer gap',
  };
}
